<script>
    import TableToggle from "../../../Components/Common/TableToggle.svelte";
    import {
        ErrorModal,
        Loader,
        SvelteTable,
        NotificationModal,
        CardLayout,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";
    import {
        addIcon,
        deleteIcon,
        editIcon,
    } from "../../../Utils/utilConstants.svelte";

    import {
        getEmailValidation,
        getPhoneValidation,
        getText,
        isObjectEmpty,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";

    export let patientID = Number(0);
    export let patientContacts = [];
    export let reloadPatientContacts = () => {};

    $: isValidEmail = selectedContactObj.email
        ? getEmailValidation(selectedContactObj.email)
        : true;

    $: isValidPhone = selectedContactObj.phone
        ? getPhoneValidation(selectedContactObj.phone)
        : true;

    $: isSubmitBtnDisabled = _getSubmitBtnDisable(
        selectedContactObj,
        isValidEmail,
        isValidPhone,
    );

    $: isSelectedObjContactDefault =
        !isObjectEmpty(prevContactObj) && prevContactObj.useAsContact;

    let isAddContactModalOpen = false,
        isEditContactModalOpen = false,
        isDeleteContactModalOpen = false,
        selectedContactObj = {},
        prevContactObj = {},
        isModifyingContact = false;

    let isHandleSetContactDefaultModalOpen = false,
        isDefaultContactChanged = false;

    const columns = [
        {
            key: "name",
            title: "Name",
            headerClass: "text-left pl-3 cursor-default w-[15%] rounded-tl-xl",
            class: "text-left px-3 cursor-default w-[15%]",
            renderValue: (v) => getText(v.name),
        },
        {
            key: "relationship",
            title: "Relationship",
            headerClass: "text-left pl-3 cursor-default w-[25%]",
            class: "text-left px-3 cursor-default w-[25%]",
            renderValue: (v) => getText(v.relationship),
        },
        {
            key: "email",
            title: "Email",
            headerClass: "text-left pl-3 cursor-default w-[20%]",
            class: "text-left px-3 cursor-default w-[20%]",
            renderValue: (v) => getText(v.email),
        },
        {
            key: "phone",
            title: "Contact No.",
            headerClass: "text-left pl-3 cursor-default w-[15%]",
            class: "text-left px-3 cursor-default w-[15%]",
            renderValue: (v) => getText(v.phone),
        },
        {
            key: "isDefault",
            title: "Default Contact Person",
            headerClass: "text-left pl-3 text-sm cursor-default w-[15%]",
            class: "text-left px-3 cursor-default w-[15%]",
            renderComponent: {
                component: TableToggle,
                props: {
                    valueKey: "useAsContact",
                    clickFunction: async (data) => updateDefaultContact(data),
                },
            },
        },
        {
            key: "buttons",
            title: "",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[2%]",
            class: "text-left px-3 cursor-default",
            renderValue: (v) => _renderActionIcons(v.useAsContact),
        },
    ];

    const btnOptions = [
        {
            iconName: "addIcon",
            hasTooltip: false,
            iFont: addIcon,
            btnText: "Add",
            iconFunction: () => _openCreateContactModal(),
        },
    ];

    const modifyContact = async (type = "create") => {
        isModifyingContact = true;

        selectedContactObj = { ...selectedContactObj, patientId: patientID };

        if (isDefaultContactChanged) await revertDefaultContact();

        switch (type) {
            case "create":
                await createContact();
                break;
            case "edit":
                await updateContact();
                break;
            case "delete":
                await deleteContact();
                break;
            default:
                break;
        }

        _closeModifyModal();

        isModifyingContact = false;
    };

    const updateDefaultContact = async (data) => {
        // if (data.useAsContact) return;

        // await revertDefaultContact();

        selectedContactObj = { ...data, useAsContact: !data.useAsContact };

        await updateContact();

        selectedContactObj = {};
    };

    const revertDefaultContact = async () => {
        const defaultContact = patientContacts.find(
            (contact) => contact.useAsContact,
        );

        if (defaultContact)
            await apiMethods.putMethod.updatePatientContact({
                ...defaultContact,
                useAsContact: false,
            });
    };

    const createContact = async () => {
        await apiMethods.postMethod
            .createPatientContact(selectedContactObj)
            .then((res) => _handleModifyContact(res))
            .catch((err) => showException(err));
    };

    const updateContact = async () => {
        await apiMethods.putMethod
            .updatePatientContact(selectedContactObj)
            .then((res) => _handleModifyContact(res, "update"))
            .catch((err) => showException(err));
    };

    const deleteContact = async () => {
        await apiMethods.deleteMethod
            .deletePatientContact(selectedContactObj.id)
            .then((res) => _handleModifyContact(res, "delete"))
            .catch((err) => showException(err));
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "buttons") {
            if (className.includes("editIcon")) _openEditContactModal(rowData);
            else if (className.includes("deleteIcon"))
                _openDeleteContactModal(rowData);
        } else if (key == "isDefault") {
            return;
        } else if (rowData.id) _openEditContactModal(rowData);
    };

    // HELPER FUNCTIONS
    const _handleModifyContact = (res, type = "create") => {
        if (!res || res.error)
            showError(
                `Failed to ${type} patient contact, please refresh the page and try again and contact support if the issue persists.`,
            );
        else reloadPatientContacts();
    };

    const _handleChangeDefaultContact = () => {
        const alreadyHasDefault = patientContacts.some(
            (contact) =>
                contact.useAsContact && contact.id != selectedContactObj.id,
        );

        if (isAddContactModalOpen) {
            if (!alreadyHasDefault) return;
            if (selectedContactObj.useAsContact)
                _openChangeDefaultContactModal();
        } else if (isEditContactModalOpen) {
            if (!prevContactObj.useAsContact && selectedContactObj.useAsContact)
                _openChangeDefaultContactModal();
        }
    };

    const _renderActionIcons = (useAsContact) => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Update Contact">
                        <i class="editIcon ${editIcon}"></i>
                    </div>
                    ${
                        useAsContact
                            ? ""
                            : `<div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Contact">
                        <i class="deleteIcon ${deleteIcon}"></i>
                    </div>`
                    }
                </div>`;
    };

    const _openCreateContactModal = () => {
        selectedContactObj.useAsContact = _getContactDefaultUse();
        isAddContactModalOpen = true;
    };

    const _openEditContactModal = (obj) => {
        selectedContactObj.useAsContact = _getContactDefaultUse();
        selectedContactObj = { ...obj };
        prevContactObj = { ...selectedContactObj };
        isEditContactModalOpen = true;
    };

    const _openDeleteContactModal = (obj) => {
        selectedContactObj = { ...obj };
        isDeleteContactModalOpen = true;
    };

    const _closeModifyModal = () => {
        isAddContactModalOpen = false;
        isEditContactModalOpen = false;
        isDeleteContactModalOpen = false;
        isDefaultContactChanged = false;
        selectedContactObj = {};
        prevContactObj = {};
    };

    const _openChangeDefaultContactModal = () =>
        (isHandleSetContactDefaultModalOpen = true);

    const _closeChangeDefaultContactModal = (isChange = false) => {
        isDefaultContactChanged = isChange;
        selectedContactObj.useAsContact = isChange;
        isHandleSetContactDefaultModalOpen = false;
    };

    const _getContactDefaultUse = () => {
        const isNewObj = !selectedContactObj.hasOwnProperty("id");

        if (!isNewObj) return selectedContactObj.useAsContact;

        if (!patientContacts.length) return true;
    };

    const _getSubmitBtnDisable = (obj, validEmail, validPhone) => {
        if (!validEmail || !validPhone) return true;

        return !obj.name;
    };
</script>

<CardLayout title="Patient Contacts" {btnOptions} id="contactsTable">
    {#if patientContacts.length}
        <SvelteTable
            {columns}
            rows={patientContacts}
            clickCellFunc={({ detail: { event, row, key } }) => {
                handleClickCell(event, row, key);
            }}
        />
    {:else}
        <div
            class="flex flex-1 items-center justify-between px-2 py-4 w-full lg:flex-row"
        >
            <p>No contacts currently under this patient</p>
        </div>
    {/if}
</CardLayout>

<NotificationModal
    isModalOpen={isAddContactModalOpen || isEditContactModalOpen}
    title={`${isAddContactModalOpen ? "Add" : "Update"} Patient Contact`}
    closeModal={_closeModifyModal}
    cancelText="Cancel"
    isConfirming={isModifyingContact}
    showConfirmBtn={false}
    closeModalFunction={_closeModifyModal}
    btnOptions={[
        {
            text: isModifyingContact
                ? "Confirming"
                : isAddContactModalOpen
                  ? "Create"
                  : "Update",
            loading: isModifyingContact || isSubmitBtnDisabled,
            function: () =>
                modifyContact(isAddContactModalOpen ? "create" : "edit"),
        },
    ]}
>
    <div class="flex flex-col w-full gap-y-2">
        <div class="flex gap-x-4">
            <p class="w-36">Name:</p>
            <input
                type="text"
                class="input input-sm input-bordered flex-1"
                autocomplete="off"
                bind:value={selectedContactObj.name}
            />
        </div>

        <div class="flex gap-x-4">
            <p class="w-36">Relationship:</p>
            <input
                type="text"
                class="input input-sm input-bordered flex-1"
                autocomplete="off"
                bind:value={selectedContactObj.relationship}
            />
        </div>

        <div class="flex gap-x-4">
            <p class="w-36">Email:</p>
            <input
                type="email"
                class="input input-sm input-bordered flex-1"
                autocomplete="off"
                bind:value={selectedContactObj.email}
            />
        </div>

        {#if !isValidEmail}
            <div class="flex gap-x-4">
                <p class="w-36" />
                <p class="text-error">Invalid Email</p>
            </div>
        {/if}

        <div class="flex gap-x-4">
            <p class="w-36">Phone:</p>
            <input
                type="text"
                class="input input-sm input-bordered flex-1"
                autocomplete="off"
                bind:value={selectedContactObj.phone}
                on:blur={() =>
                    (selectedContactObj.phone =
                        selectedContactObj.phone?.replaceAll(" ", ""))}
            />
        </div>

        {#if !isValidPhone}
            <div class="flex gap-x-4">
                <p class="w-36" />
                <p class="text-error">Invalid Phone</p>
            </div>
        {/if}

        <div class="flex gap-x-4">
            <p class="w-48">Use for communications:</p>
            <input
                type="checkbox"
                class="checkbox checkbox-primary"
                bind:checked={selectedContactObj.useAsContact}
                on:change={_handleChangeDefaultContact}
                disabled={isSelectedObjContactDefault}
            />
        </div>
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isDeleteContactModalOpen}
    title="Delete patient contact"
    cancelText="Delete"
    submitText="Cancel"
    confirmButtonClass="btn-accent"
    isReverseBtns
    confirmAction={_closeModifyModal}
    closeModalFunction={_closeModifyModal}
    closeModal={() => modifyContact("delete")}
    isConfirming={isModifyingContact}
>
    Delete selected patient contact details?
</NotificationModal>

<NotificationModal
    isModalOpen={isHandleSetContactDefaultModalOpen}
    title="Default contact already set"
    cancelText="Cancel"
    closeModalFunction={() => _closeChangeDefaultContactModal(false)}
    confirmAction={() => _closeChangeDefaultContactModal(true)}
    closeModal={() => _closeChangeDefaultContactModal(false)}
>
    This patient already has a default contact, do you want to replace with this one?
</NotificationModal>
